import { useCallback, useState } from "react";
import { useEntitySearch } from "@/lib/useEntitySearch";
import { PaginationConfig } from "@/components/ui/pagination-footer";

/** Default rows per page for paginated list views. */
export const DEFAULT_PAGE_SIZE = 50;

interface UsePaginatedEntitySearchConfig<T extends { id: string }> {
  entities: Map<string, T>;
  fetchEntities: (params: {
    search?: string;
    limit: number;
    offset: number;
  }) => Promise<{ ids: string[]; total: number }>;
  pageSize?: number;
}

/**
 * Wraps useEntitySearch with offset pagination for list controllers: owns the
 * page/total/search state, resets to the first page on search, and exposes the
 * DataTable pagination config plus page-aware refetch helpers.
 */
export function usePaginatedEntitySearch<T extends { id: string }>({
  entities,
  fetchEntities,
  pageSize = DEFAULT_PAGE_SIZE,
}: UsePaginatedEntitySearchConfig<T>) {
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [currentSearch, setCurrentSearch] = useState("");

  const {
    entitiesArray,
    handleSearch: baseHandleSearch,
    setDisplayedEntityIds,
  } = useEntitySearch({
    entities,
    fetchEntities: async (params) => {
      const result = await fetchEntities({
        search: params?.search,
        limit: pageSize,
        offset: page * pageSize,
      });
      setTotal(result.total);
      return result.ids;
    },
  });

  /**
   * Resets to the first page and re-queries the full dataset for the search term.
   */
  const handleSearch = useCallback(
    (value: string) => {
      setPage(0);
      setCurrentSearch(value);
      baseHandleSearch(value);
    },
    [baseHandleSearch],
  );

  /**
   * Fetches a specific page with the current search and updates the displayed rows.
   */
  const fetchPage = useCallback(
    async (targetPage: number) => {
      setPage(targetPage);
      const result = await fetchEntities({
        search: currentSearch || undefined,
        limit: pageSize,
        offset: targetPage * pageSize,
      });
      setTotal(result.total);
      setDisplayedEntityIds(result.ids);
    },
    [fetchEntities, currentSearch, pageSize, setDisplayedEntityIds],
  );

  /**
   * Refetches after a row was removed, clamping to the last non-empty page.
   */
  const refetchAfterRemoval = useCallback(() => {
    const newTotal = Math.max(0, total - 1);
    const maxPage = Math.max(0, Math.ceil(newTotal / pageSize) - 1);
    return fetchPage(Math.min(page, maxPage));
  }, [fetchPage, page, total, pageSize]);

  const pagination: PaginationConfig = {
    page,
    pageSize,
    total,
    onPageChange: fetchPage,
  };

  return {
    entitiesArray,
    handleSearch,
    setDisplayedEntityIds,
    page,
    fetchPage,
    refetchAfterRemoval,
    pagination,
  };
}
